import { useApiQuery } from "@gladis/api-client";
import { formatEur, type MemberDto, type SavingsAccountDetailDto } from "@gladis/shared";
import { EmptyState, ErrorState, LoadingState, PersonLabel, Table } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import { formatDay } from "./format-day.js";

export const savingsAccountKey = (accountId: string) => ["savings", "account", accountId] as const;

/**
 * One account's full balance history, newest first (FR-025).
 *
 * THE DATE IS FORMATTED FROM THE STRING, never by building a `Date` from it — see `formatDay`, which
 * now lives beside this file because the stat row needs the same guarantee.
 *
 * FUTURE-DATED ROWS ARE SHOWN AND MARKED. They are part of the record, and a member who has just
 * entered one needs to see it. The mark is what explains why the balance at the top does not match
 * the first row.
 */
export function SnapshotHistory({
  accountId,
  members = [],
}: {
  accountId: string;
  /** The household's members, for their faces (020). */
  members?: readonly MemberDto[];
}): ReactNode {
  const t = useT();
  const detail = useApiQuery<SavingsAccountDetailDto>(
    savingsAccountKey(accountId),
    `/savings/accounts/${accountId}`,
  );

  /*
   * A 404 IS NOT AN ERROR, IT IS AN ANSWER (018's FR-016).
   *
   * Until the activity feed existed, this page was only ever reached by clicking a row in a list, so the
   * record was there by construction. The feed can now link to a record that has since been hard-deleted —
   * in practice a stock removed while it had no transactions — and the feed deliberately does not check
   * (that would be a query per row on every page). So the destination reports it, and "we couldn't load
   * this" is the wrong sentence for a record that is simply gone.
   */
  if (detail.isPending) return <LoadingState>{t("savings.history.loading")}</LoadingState>;
  if (detail.error?.status === 404) return <EmptyState>{t("savings.history.missing")}</EmptyState>;
  if (detail.isError) return <ErrorState>{t("savings.history.loadFailed")}</ErrorState>;

  const { account, snapshots } = detail.data;
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <section>
      <header className="mb-6">
        <p className="text-sm text-muted">
          {account.ownerLabel ?? t("person.joint")}
          {account.archivedAt ? " · Archived" : ""}
        </p>
        <h2 className="text-2xl font-semibold">{account.name}</h2>
        <p className="mt-2 text-3xl font-semibold" data-testid="account-balance">
          {formatEur(account.currentBalance)}
        </p>
      </header>

      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeaderCell>{t("savings.history.colAsOf")}</Table.HeaderCell>
            <Table.HeaderCell>{t("savings.history.colRecordedBy")}</Table.HeaderCell>
            <Table.HeaderCell numeric>{t("savings.history.colBalance")}</Table.HeaderCell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {snapshots.map((snapshot) => (
            <Table.Row key={snapshot.id}>
              <Table.Cell>
                {formatDay(snapshot.asOfDate)}
                {snapshot.asOfDate > todayIso ? (
                  <span className="ml-2 text-sm text-muted">upcoming</span>
                ) : null}
              </Table.Cell>
              {/*
                "A former member" is a STAND-IN, not a name — nothing is snapshotted beside
                `recorded_by`, so once that account is deleted the row genuinely does not know who
                it was about. `isPerson` is false there and no mark is drawn, because initials from
                that phrase would read "AM" and belong to nobody (FR-024).
              */}
              <Table.Cell>
                <PersonLabel
                  label={snapshot.recordedByLabel ?? t("person.formerMember")}
                  isPerson={snapshot.recordedByLabel !== null}
                  avatarUrl={members.find((m) => m.userId === snapshot.recordedById)?.avatarUrl}
                />
              </Table.Cell>
              <Table.Cell numeric>{formatEur(snapshot.balance)}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <p className="mt-4 text-sm text-muted">{t("savings.history.note")}</p>
    </section>
  );
}
